import { Info } from "lucide-react";
import { type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from "react";

/**
 * The why behind a control, one tap or hover away instead of a line of prose
 * under it. Hover opens it on a mouse; a tap or Enter pins it open for touch
 * and keyboard; Escape, a click elsewhere or leaving closes it.
 *
 * Fixed-positioned against the icon and clamped to the viewport, so it
 * stays readable next to the right edge of a phone screen and inside the
 * rounded, overflow-hidden boxes the settings rows sit in.
 */
export function InfoTip({ children, label = "More about this" }: { children: ReactNode; label?: string }) {
	const [hover, setHover] = useState(false);
	const [pinned, setPinned] = useState(false);
	const open = hover || pinned;
	const button = useRef<HTMLButtonElement>(null);
	const panel = useRef<HTMLDivElement>(null);
	const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
	const id = useId();

	useLayoutEffect(() => {
		if (!open || !button.current) return;
		const place = () => {
			const r = button.current!.getBoundingClientRect();
			const width = Math.min(288, window.innerWidth - 24);
			const left = Math.min(Math.max(12, r.left + r.width / 2 - width / 2), window.innerWidth - width - 12);
			const below = r.bottom + 6;
			const height = panel.current?.offsetHeight ?? 0;
			const top = below + height > window.innerHeight - 12 ? Math.max(12, r.top - 6 - height) : below;
			setPos({ top, left });
		};
		place();
		window.addEventListener("scroll", place, true);
		window.addEventListener("resize", place);
		return () => {
			window.removeEventListener("scroll", place, true);
			window.removeEventListener("resize", place);
		};
	}, [open]);

	useEffect(() => {
		if (!pinned) return;
		const onDown = (e: MouseEvent) => {
			const t = e.target as Node;
			if (!button.current?.contains(t) && !panel.current?.contains(t)) setPinned(false);
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setPinned(false);
		};
		document.addEventListener("mousedown", onDown);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDown);
			document.removeEventListener("keydown", onKey);
		};
	}, [pinned]);

	return (
		<>
			<button
				ref={button}
				type="button"
				aria-label={label}
				aria-expanded={open}
				aria-describedby={open ? id : undefined}
				onClick={() => setPinned((p) => !p)}
				onMouseEnter={() => setHover(true)}
				onMouseLeave={() => setHover(false)}
				className="mx-1 inline-flex size-5 items-center justify-center rounded-full align-middle text-muted hover:bg-raised hover:text-ink"
			>
				<Info className="size-3.5" aria-hidden />
			</button>
			{open && (
				<div
					ref={panel}
					id={id}
					role="tooltip"
					onMouseEnter={() => setHover(true)}
					onMouseLeave={() => setHover(false)}
					style={{
						position: "fixed",
						top: pos?.top ?? -9999,
						left: pos?.left ?? -9999,
						width: Math.min(288, typeof window === "undefined" ? 288 : window.innerWidth - 24),
					}}
					className="z-50 rounded-lg border border-hairline bg-paper px-3 py-2 text-xs leading-relaxed font-normal text-muted shadow-[0_12px_32px_-12px_rgba(6,9,18,0.45)]"
				>
					{children}
				</div>
			)}
		</>
	);
}
